import dayjs from 'dayjs';
import avatar from 'images/avatar.png';
import company from 'images/company.png';

import { DISPLAY_DATE_TIME_FORMAT } from 'constants/common';
import { cdnEndpoint } from 'constants/endpoints';

import { FilterValue } from 'components/Table/types';

import { getEnumValues } from 'utils/helper';

import {
  APIOrganization,
  APIPayment,
  APIPaymentStatus,
  APIUser,
  APIUserOrganization,
  APIUserRight,
  APIUserType,
} from 'types/api';

export const userCanDownloadCheck = (item: APIPayment) => {
  return (
    item.status === APIPaymentStatus.OpenToPrint ||
    item.status === APIPaymentStatus.TryToPrint ||
    item.status === APIPaymentStatus.CouldNotPrint ||
    item.status === APIPaymentStatus.Printed ||
    item.status === APIPaymentStatus.Delivered ||
    item.status === APIPaymentStatus.NotDelivered ||
    item.status === APIPaymentStatus.Emailed
  );
};

export const getAvatarUrl = (user: APIUser) => {
  if (!user.avatar) {
    return avatar;
  }
  return `${cdnEndpoint()}files/${user.id}/images/${user.avatar}`;
};

export const getOrganizationAvatarUrl = (
  organization?: APIOrganization | undefined,
) => {
  if (!organization || !organization.avatar) {
    return company;
  }
  return `${cdnEndpoint()}files/${organization.id}/images/${
    organization.avatar
  }`;
};

export const convertUrlToFile = async (url: string): Promise<File | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Failed to fetch avatar image:', response.statusText);
      return null;
    }

    const blob = await response.blob();
    const fileName = url.split('/').pop() || 'profile-picture.png';
    const fileType = blob.type || 'image/png';
    return new File([blob], fileName, { type: fileType });
  } catch (error) {
    console.error('Error converting avatar URL to file:', error);
    return null;
  }
};

export const downloadExportFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

export const moneyFormatter = (money: string | number | null) => {
  const formattedMoney = parseFloat(money?.toString() ?? '0').toLocaleString(
    'en-US',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  );

  return `$${formattedMoney}`;
};

export const camelCaseHumanized = (str: string): string => {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
};

export const assignableUserRights = getEnumValues(APIUserRight).filter(
  it => it !== APIUserRight.SuperAdmin,
);

export const hasRights = (
  organizations: APIUserOrganization[],
  userRights: APIUserRight,
  organizationId?: string,
) => {
  if (!organizations || organizations.length === 0) {
    return false;
  }

  const userOrganization = organizations?.find(
    it => it.organization.id == organizationId,
  );

  const userType = userOrganization?.userType;
  const rights = userOrganization?.userRights.map(it => it.right) || [];

  // A super admin viewing an external organization (synthetic membership) has
  // full access, same as an organization admin.
  return (
    userType == APIUserType.Admin ||
    userType == APIUserType.SuperAdmin ||
    rights.includes(userRights)
  );
};

export const humanizePascalCase = (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const mergeSavedColumnsWithExisting = (
  savedColumns: Array<any>,
  columns: Array<any>,
) => {
  return savedColumns.map((savedColumn: any) => {
    const existingColumn = columns.find(
      (column: any) => column.dataId === savedColumn.dataId,
    );
    return { ...existingColumn, ...savedColumn };
  });
};

const hasFilterValueContent = (filterValue: FilterValue) => {
  if (filterValue == null) {
    return false;
  }
  if (typeof filterValue === 'string') {
    return filterValue !== '';
  }
  const hasValue = filterValue.value != null && filterValue.value !== '';
  const valueTo = 'valueTo' in filterValue ? filterValue.valueTo : undefined;
  return hasValue || (valueTo != null && valueTo !== '');
};

// Simple filters live on `column.filter`, advanced (string/numeric/date) ones on
// `column.filterValue`, so both have to be checked to know a column is filtered.
export const hasActiveColumnFilter = (column: {
  filter?: string;
  filterValue?: FilterValue;
}) => !!column.filter || hasFilterValueContent(column.filterValue);

export const extractFiltersFromSavedColumns = (savedColumns: any[]) =>
  savedColumns.filter(hasActiveColumnFilter).reduce((acc, it) => {
    acc[it.dataId] = it.filter || it.filterValue;
    return acc;
  }, {});

export const convertToUtcIsoString = (
  dateTimeValue: string | null | undefined,
  timezone: string,
): string | null => {
  if (!dateTimeValue) return null;

  // Get the "wall clock" time from the picker (what the user sees)
  const pickerValue = dayjs(dateTimeValue);
  const dateStr = pickerValue.format('YYYY-MM-DD HH:mm:ss');

  return dayjs.tz(dateStr, timezone).utc().toISOString();
};

export const convertFromUtcToTimezone = (
  utcDateTimeValue: string | null | undefined,
  timezone: string,
): string | null => {
  if (!utcDateTimeValue) return null;

  // Convert UTC datetime to the specified timezone
  return dayjs.utc(utcDateTimeValue).tz(timezone).format('YYYY-MM-DDTHH:mm:ss');
};

export const formatDateTime = (value: string | null | undefined): string => {
  return value ? dayjs(value).format(DISPLAY_DATE_TIME_FORMAT) : '';
};

export const formatDateTimeWithTimezone = (
  value: string | null | undefined,
  timezone: string,
): string => {
  return value
    ? dayjs(value).tz(timezone).format(DISPLAY_DATE_TIME_FORMAT)
    : '';
};
